import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { authGuard, homePageGuard } from '@core/guards';
import { ChunkLoadErrorHandlerModule } from '@features/chunk-load-error-handler';
import { accessPageGuard } from '@features/security';
import {
  accountGuard,
  deactivateUserPageGuard,
  externalGuard,
  internalGuardMatch,
  noRoleAccountGuard,
} from '@features/server-side-authentication';
import { SwaggerViewComponent } from '@features/swagger-view/components';
import {
  ExternalAccessLayoutComponent,
  HomeComponent,
  InternalAccessLayoutComponent,
  MainComponent,
  Page403Component,
  Page404Component,
  PageNoRoleComponent,
  PageReloadComponent,
  PageUnknownErrorComponent,
  UnAuthenticatedComponent,
} from './layout/components';

export const routes: Routes = [
  {
    path: 'role-not-assigned',
    component: PageNoRoleComponent,
    canActivate: [noRoleAccountGuard],
    pathMatch: 'full',
  },
  {
    path: 'auth',
    loadChildren: () => import('@features/authentication').then((mod) => mod.AuthenticationModule),
  },
  {
    path: '',
    component: MainComponent,
    canActivate: [accountGuard, authGuard],
    canDeactivate: [deactivateUserPageGuard],
    children: [
      {
        path: '',
        component: InternalAccessLayoutComponent,
        canMatch: [accountGuard, internalGuardMatch],
        canDeactivate: [deactivateUserPageGuard],
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: 'organizations',
          },
          {
            path: 'organizations',
            loadChildren: () =>
              import('@features/organizations')
                .then((mod) => mod.OrganizationsModule)
                .catch(() => {
                  return ChunkLoadErrorHandlerModule;
                }),
          },
        ],
      },
      {
        path: '',
        component: ExternalAccessLayoutComponent,
        canActivate: [externalGuard],
        canDeactivate: [deactivateUserPageGuard],
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: 'home',
          },
          {
            path: 'user',
            loadChildren: () =>
              import('@features/user')
                .then((mod) => mod.UserModule)
                .catch(() => {
                  return ChunkLoadErrorHandlerModule;
                }),
          },
          {
            path: 'home',
            canActivate: [homePageGuard],
            component: HomeComponent,
            pathMatch: 'full',
          },
          {
            path: 'projects',
            loadChildren: () =>
              import('@features/projects')
                .then((mod) => mod.ProjectsModule)
                .catch(() => {
                  return ChunkLoadErrorHandlerModule;
                }),
            canActivate: [accessPageGuard],
            data: {
              permissions: ['view_projects'],
            },
          },
          {
            path: 'data-sources',
            loadChildren: () =>
              import('@features/data-sources')
                .then((mod) => mod.DataSourcesModule)
                .catch(() => {
                  return ChunkLoadErrorHandlerModule;
                }),
            canActivate: [accessPageGuard],
            data: {
              permissions: ['view_datasource'],
            },
          },
          {
            path: 'user-management',
            loadChildren: () =>
              import('@features/user-management')
                .then((mod) => mod.UserManagementModule)
                .catch(() => {
                  return ChunkLoadErrorHandlerModule;
                }),
            canActivate: [accessPageGuard],
            data: {
              permissions: ['view_user'],
            },
          },
          {
            path: 'role-management',
            loadChildren: () =>
              import('@features/role-management')
                .then((mod) => mod.RoleManagementModule)
                .catch(() => {
                  return ChunkLoadErrorHandlerModule;
                }),
            canActivate: [accessPageGuard],
            data: {
              permissions: ['view_role'],
            },
          },
          {
            path: 'categories',
            loadChildren: () =>
              import('@features/categories')
                .then((mod) => mod.CategoriesModule)
                .catch(() => {
                  return ChunkLoadErrorHandlerModule;
                }),
            canActivate: [accessPageGuard],
            data: {
              permissions: ['view_category'],
            },
          },
          {
            path: 'connectors',
            loadChildren: () =>
              import('@features/connectors')
                .then((mod) => mod.ConnectorsModule)
                .catch(() => {
                  return ChunkLoadErrorHandlerModule;
                }),
            canActivate: [accessPageGuard],
            data: {
              permissions: ['view_connector'],
            },
          },
          {
            path: 'pipelines',
            loadChildren: () =>
              import('@features/pipelines')
                .then((mod) => mod.PipelinesModule)
                .catch(() => {
                  return ChunkLoadErrorHandlerModule;
                }),
            canActivate: [accessPageGuard],
            data: {
              permissions: ['view_pipeline'],
            },
          },
          {
            path: 'audit-log',
            loadChildren: () =>
              import('@features/audit-log')
                .then((mod) => mod.AuditLogModule)
                .catch(() => {
                  return ChunkLoadErrorHandlerModule;
                }),
            canActivate: [accessPageGuard],
            data: {
              permissions: ['view_auditlog'],
            },
          },
          {
            path: 'notifications',
            loadChildren: () =>
              import('@features/notification-management')
                .then((mod) => mod.NotificationManagementModule)
                .catch(() => {
                  return ChunkLoadErrorHandlerModule;
                }),
            canActivate: [accessPageGuard],
            data: {
              permissions: ['view_notifications'],
            },
          },
          {
            path: 'vpn-configuration',
            loadChildren: () =>
              import('@features/vpn-configuration')
                .then((mod) => mod.VpnConfigurationModule)
                .catch(() => {
                  return ChunkLoadErrorHandlerModule;
                }),
            canActivate: [accessPageGuard],
            data: {
              permissions: ['view_vpnconfig'],
            },
          },
          {
            path: 'departments',
            loadChildren: () =>
              import('@features/departments')
                .then((mod) => mod.DepartmentsModule)
                .catch(() => {
                  return ChunkLoadErrorHandlerModule;
                }),
            canActivate: [accessPageGuard],
            data: {
              permissions: ['view_department'],
            },
          },
        ],
      },
      {
        path: 'un-authenticated',
        component: UnAuthenticatedComponent,
      },
      {
        path: 'no-access',
        component: Page403Component,
      },
    ],
  },
  {
    path: 'sso',
    loadChildren: () =>
      import('@features/sso-provision')
        .then((mod) => mod.SsoProvisionModule)
        .catch(() => {
          return ChunkLoadErrorHandlerModule;
        }),
  },
  {
    path: 'swagger',
    canActivate: [accountGuard, authGuard],
    canDeactivate: [deactivateUserPageGuard],
    component: SwaggerViewComponent,
  },
  {
    path: 'reload',
    component: PageReloadComponent,
  },
  {
    path: '',
    pathMatch: 'full',
    redirectTo: '/auth/login',
  },
  {
    path: 'unknown-error',
    component: PageUnknownErrorComponent,
  },
  {
    path: '**',
    component: Page404Component,
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
